/*
 * The offline base map (scripts/basemap): which version is current, where its
 * PMTiles archive is, and the arithmetic of saving an area for offline use.
 * Nothing heavy is imported here: the PMTiles reader and the vector layer
 * live in ./vector.js, loaded only when a map needs them (the `maps-vector`
 * chunk).
 *
 *   basemap/current.json            {format: 'pitriot-basemap', version, manifest}
 *   basemap/<version>/manifest.json {version, file, bytes, sha256, bbox, maxzoom, attribution, ...}
 *   basemap/<version>/israel.pmtiles
 *
 * The pointer is asked afresh each time (cache: no-store); the last one seen
 * is kept, so a phone with no signal still opens the archive it saved.
 */
const LAST = 'mb_basemap_pointer';
const AREAS = 'mb_offline_areas';
export const SAVE_MIN_ZOOM = 8;
export const SAVE_MAX_ZOOM = 15;
export const DEFAULT_RADIUS_KM = 10;

export class BasemapUnavailable extends Error {}

function baseHref() {
  return typeof location !== 'undefined' && location.href ? location.href : 'http://localhost/';
}

/** {version, url, maxzoom, bbox, attribution} of the current base map, or the last one seen. */
export async function readBasemap(pointerUrl, { fetchFn = fetch, storage = globalThis.localStorage } = {}) {
  try {
    const res = await fetchFn(pointerUrl, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const pointer = await res.json();
    if (pointer?.format !== 'pitriot-basemap' || !pointer.manifest) throw new Error('not a base map pointer');
    const manifestUrl = new URL(pointer.manifest, new URL(pointerUrl, baseHref())).href;
    const mres = await fetchFn(manifestUrl);
    if (!mres.ok) throw new Error(`HTTP ${mres.status}`);
    const manifest = await mres.json();
    const info = {
      version: manifest.version, url: new URL(manifest.file, manifestUrl).href, maxzoom: manifest.maxzoom ?? SAVE_MAX_ZOOM,
      bbox: manifest.bbox || null, attribution: manifest.attribution || '© OpenStreetMap contributors',
      avgTileBytes: manifest.avg_tile_bytes || null,
    };
    try { storage?.setItem(LAST, JSON.stringify(info)); } catch (e) { /* not kept */ }
    return info;
  } catch (e) {
    try {
      const last = JSON.parse(storage?.getItem(LAST) || 'null');
      if (last?.version && last?.url) return last;
    } catch (err) { /* nothing kept */ }
    throw new BasemapUnavailable('no offline base map on this device or the server');
  }
}

/** [south, west, north, east] of a square `radiusKm` around a point. */
export function areaAround({ lat, lng }, radiusKm = DEFAULT_RADIUS_KM) {
  const dLat = radiusKm / 111.32;
  const dLng = radiusKm / (111.32 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
  return [lat - dLat, lng - dLng, lat + dLat, lng + dLng];
}

const lng2x = (lng, z) => Math.floor(((lng + 180) / 360) * 2 ** z);
const lat2y = (lat, z) => {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
};

/** Every z/x/y tile of a bbox from zoom `zmin` to `zmax`. */
export function tilesInArea([south, west, north, east], zmin = SAVE_MIN_ZOOM, zmax = SAVE_MAX_ZOOM) {
  const out = [];
  for (let z = zmin; z <= zmax; z += 1) {
    const [x0, x1] = [lng2x(west, z), lng2x(east, z)];
    const [y0, y1] = [lat2y(north, z), lat2y(south, z)];
    for (let x = x0; x <= x1; x += 1) for (let y = y0; y <= y1; y += 1) out.push([z, x, y]);
  }
  return out;
}

/** The saved areas this device remembers: [{id, label, bbox, savedAt, tiles, bytes, version}]. */
export function savedAreas(storage = globalThis.localStorage) {
  try {
    const list = JSON.parse(storage?.getItem(AREAS) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

export function rememberArea(area, storage = globalThis.localStorage) {
  const list = savedAreas(storage).filter((a) => a.id !== area.id);
  list.unshift(area);
  try { storage?.setItem(AREAS, JSON.stringify(list.slice(0, 20))); } catch (e) { /* not kept */ }
  return list;
}

export function forgetAreas(storage = globalThis.localStorage) {
  try { storage?.removeItem(AREAS); } catch (e) { /* nothing kept */ }
}
